// Core
import { Auth } from '@aws-amplify/auth';
import { API } from '@aws-amplify/api';
import axios from 'axios';

// Components
import Message from '../layout/elements/Message';

// Store
import { performLogout } from './auth';

/**
 * Triggers a default error message
 * @returns {Object} The default error message
 */
export const defaultErrorMsg = () => Message.error('Sorry, there was an error!');

/**
 * Handles the request error based on HTTP Status
 */
const handleRequestError = (err, showError = true) => {
  if (err.response) {
    switch (err.response.status) {
      case 401:
        performLogout();
        break;
      case 403:
        if (showError) {
          Message.warning('Access forbidden!');
        }
        break;
      case 422:
        if (showError) {
          if (!Array.isArray(err.response.data)) {
            Message.warning(err.response.data);
          }
        }
        break;
      case 404:
        if (showError) {
          defaultErrorMsg();
        }
        break;
      default:
        if (showError) {
          defaultErrorMsg();
        }
        break;
    }
  } else if (err === 'No current user') {
    performLogout();
  } else if (showError) {
    defaultErrorMsg();
  }
  throw err;
};

/**
 * Performs an API request to Vantage Mgnt APIs
 * @param apiName {string} The API's name
 * @param method {string} The request method
 * @param path {string} The relative path in the API
 * @param data {object} Data to send in the request
 * @param showError {boolean} If function must handle error messages
 * @param auth {boolean} If request is authenticated
 * @returns {Promise<Object>} Axios response
 */
const performVantageApiRequest = async (apiName, method, path, data, showError = true, auth = true) => {
  let requestMethod = method;

  // Cognito tokens
  const tokens = auth ? await Auth.currentSession().catch((err) => handleRequestError(err)) : null;

  // The config
  const config = {
    response: true,
    headers: auth ? { Authorization: tokens.idToken.jwtToken } : {},
  };

  // Data parsing
  if (data) {
    if (requestMethod === 'get') config.queryStringParameters = data;
    else if (requestMethod === 'post' || method === 'put') config.body = data;
  }

  // Method parsing
  if (requestMethod === 'delete') requestMethod = 'del';

  // Performs the request
  return API[requestMethod](apiName, path, config).catch((err) => {
    handleRequestError(err, showError);
  });
};

/**
 * Performs an API request to Vantage customer APIs
 * @param apiSubdomain {string} The API's subdomain
 * @param method {string} The request method
 * @param path {string} The relative path in the API
 * @param data {object} Data to send in the request
 * @param showError {boolean} If function must handle error messages
 * @param auth {boolean} If request is authenticated
 * @returns {Promise<Object>} Axios response
 */
const performVantageApiRequestChild = async (apiSubdomain, method, path, data, showError = true, auth = true) => {
  // Cognito tokens
  const tokens = auth ? await Auth.currentSession().catch((err) => handleRequestError(err)) : null;

  // The config
  const options = {
    headers: auth ? { Authorization: tokens.idToken.jwtToken } : {},
    method,
  };

  // Data parsing
  if (data) {
    if (options.method === 'get' || options.method === 'delete') options.params = data;
    else if (options.method === 'post' || method === 'put') options.data = data;
  }

  // Url
  options.url = process.env.REACT_APP_SPICE_API_URL.replace('[SUBDOMAIN]', apiSubdomain) + path;

  // Performs the request
  return axios(options).catch((err) => {
    handleRequestError(err, showError);
  });
};

export const requestVantageApi = {
  // async crusherModel(method = 'get', path = '/', data = null, showError = true, auth = true) {
  //   return performVantageApiRequest('crusherModel', method, path, data, showError, auth);
  // },
  async customer(method = 'get', path = '/', data = null, showError = true, auth = true) {
    return performVantageApiRequest('customer', method, path, data, showError, auth);
  },
  async modelRoll(method = 'get', path = '/', data = null, showError = true, auth = true) {
    return performVantageApiRequest('modelRoll', method, path, data, showError, auth);
  },
  async monitoring(method = 'get', path = '/', data = null, showError = true, auth = true) {
    return performVantageApiRequest('monitoring', method, path, data, showError, auth);
  },
  async user(method = 'get', path = '/', data = null, showError = true, auth = true) {
    return performVantageApiRequest('user', method, path, data, showError, auth);
  },
  async modelPropX(method = 'get', path = '/', data = null, showError = true, auth = true) {
    return performVantageApiRequest('modelPropX', method, path, data, showError, auth);
  },
  async application(method = 'get', path = '/', data = null, showError = true, auth = true) {
    return performVantageApiRequest('application', method, path, data, showError, auth);
  },
  async child(apiSubdomain, method = 'get', path = '/', data = null, showError = true, auth = true) {
    return performVantageApiRequestChild(apiSubdomain, method, path, data, showError, auth);
  },
};
